import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import {
  Add as AddIcon,
  Close as CloseIcon,
  GetApp as GetAppIcon,
  HelpOutline as HelpOutlineIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon
} from '@material-ui/icons';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { FetchError } from '../../../common/api/api';
import AppSnackbar from '../../../common/AppSnackbar';
import CardTitle from '../../../common/card/CardTitle';
import CardTitleContent from '../../../common/card/CardTitleContent';
import WrappedTableCell from '../../../common/WrappedTableCell';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import DocumentDownloadButton from '../../documents/components/DocumentDownloadButton';
import AssignDocumentTemplateDialog from '../dialogs/AssignDocumentTemplateDialog';
import ProductTemplateCreateDialog from '../dialogs/ProductTemplateCreateDialog';
import ProductTemplateRenameDialog from '../dialogs/ProductTemplateRenameDialog';
import {
  Product,
  ProductExportEntity,
  ProductTemplate
} from '../Product.interface';
import {
  getExportTitle,
  useDeleteProductTemplate,
  useDownloadDefaultTemplate,
  useFindProductTemplates,
  useUpdateProduct,
  useUpdateProductTemplate
} from '../Product.hooks';
import ReferenceDialog from '../../reference/ReferenceDialog';
import {
  DesignInputReference,
  DesignOutputReference,
  TraceabilityReportReference,
  UserNeedReference,
  VerificationReference,
  ValidationReference,
  eClinicalWorksReportReference,
  HazardReference
} from '../../reference/product.reference';

function getProductTemplateReference(entity: ProductExportEntity) {
  switch (entity) {
    case ProductExportEntity.UserNeed:
      return UserNeedReference;
    case ProductExportEntity.DesignInput:
      return DesignInputReference;
    case ProductExportEntity.DesignOutput:
      return DesignOutputReference;
    case ProductExportEntity.Verification:
      return VerificationReference;
    case ProductExportEntity.Validation:
      return ValidationReference;
    case ProductExportEntity.TraceabilityReport:
      return TraceabilityReportReference;
    case ProductExportEntity.eClinicalReport:
      return eClinicalWorksReportReference;
    case ProductExportEntity.Hazard:
      return HazardReference;
  }
}

const StyledTableCell = styled(TableCell)`
  .MuiButtonBase-root {
    margin-right: 8px;
  }
`;

export interface ManageTemplateCardProps {
  product: Product;
  showReference?: boolean;
  onUpdate?: () => void;
  onProductChange?: (product: Partial<Product>) => void;
}

const ManageTemplateCard: React.FC<ManageTemplateCardProps> = (
  props: ManageTemplateCardProps
) => {
  const { product, showReference, onUpdate, onProductChange } = props;
  const onUpdateRef = useRef(onUpdate);
  const entityRef = useRef<ProductExportEntity | undefined>();
  const [error, setError] = useState<FetchError | undefined>();
  const [templates, setTemplates] = useState<ProductTemplate[]>([]);
  const [clearTemplate, setClearTemplate] = useState<ProductTemplate>();
  const [updateTemplate, setUpdateTemplate] = useState<ProductTemplate>();
  const [removeTemplate, setRemoveTemplate] = useState<ProductTemplate>();
  const [createdTemplate, setCreatedTemplate] = useState<ProductTemplate>();
  const [renameTemplate, setRenameTemplate] = useState<ProductTemplate>();
  const [referenceType, setReferenceType] = useState<ProductExportEntity>();
  const [didDelete, setDidDelete] = useState(false);
  const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);
  const [dragColumn, setDragColumn] = useState<ProductTemplate>();
  const sensors = useSensors(useSensor(PointerSensor));
  const {
    updatedProduct,
    isUpdating: isUpdatingProduct,
    updateError: updateProductError,
    updateProduct
  } = useUpdateProduct(product.id);
  const {
    productTemplates,
    isLoading: isFinding,
    findProductTemplates
  } = useFindProductTemplates(product.id);
  const {
    updatedProductTemplate,
    isUpdating,
    updateError,
    updateProductTemplate
  } = useUpdateProductTemplate(
    product.id,
    clearTemplate?.id || renameTemplate?.id || 0
  );
  const {
    deletedProductTemplate,
    isDeleting,
    deleteError,
    deleteProductTemplate
  } = useDeleteProductTemplate(product.id, removeTemplate?.id || 0);
  const [
    { isLoading: isDownloading, error: downloadError, response },
    getDocument,
    downloadDocument
  ] = useDownloadDefaultTemplate();
  const isLoading =
    isFinding || isUpdating || isDeleting || isDownloading || isUpdatingProduct;
  const templateReference = referenceType
    ? getProductTemplateReference(referenceType)
    : undefined;

  const updateTemplates = useCallback((template: ProductTemplate) => {
    // update templates list
    setTemplates(templates => {
      const index = templates.findIndex(t => t.id === template.id);
      if (index < 0) {
        return templates.concat(template);
      }
      const copy = [...templates];
      copy.splice(index, 1, template);
      return copy;
    });
  }, []);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    findProductTemplates();
  }, [findProductTemplates]);

  useEffect(() => {
    // sort template list in the ui based on product templatePosition
    const sortOrder = product.templatePosition?.map(tmp => tmp.id) || [];
    const sortedTemplates =
      productTemplates &&
      productTemplates.sort((a, b) => {
        return sortOrder.indexOf(a.id) - sortOrder.indexOf(b.id);
      });
    sortedTemplates && setTemplates(sortedTemplates);
  }, [productTemplates]);

  useEffect(() => {
    if (createdTemplate) {
      onUpdateRef.current?.();
    }
  }, [createdTemplate]);

  useEffect(() => {
    if (updatedProductTemplate) {
      updateTemplates(updatedProductTemplate);
      onUpdateRef.current?.();
    }
  }, [updatedProductTemplate, updateTemplates]);

  useEffect(() => {
    if (deletedProductTemplate) {
      setDidDelete(true);
      findProductTemplates();
      onUpdateRef.current?.();
    }
  }, [deletedProductTemplate, findProductTemplates]);

  useEffect(() => {
    if (response?.ok && entityRef.current) {
      const fileName: string =
        getExportTitle(entityRef.current) + ' Template.docx';
      downloadDocument(fileName);
      entityRef.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    setError(updateError || deleteError || downloadError || updateProductError);
  }, [updateError, deleteError, downloadError, updateProductError]);

  useEffect(() => {
    if (updatedProduct) {
      onProductChange?.(updatedProduct);
    }
  }, [updatedProduct]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <Card>
          <CardTitleContent>
            <CardTitle>
              <SettingsIcon />
              Manage Templates
            </CardTitle>
            <Button
              color='primary'
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={() => setIsOpenCreateDialog(true)}
            >
              Add New Template
            </Button>
          </CardTitleContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Name</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell width={1}>Action</TableCell>
                </TableRow>
              </TableHead>
              <DndContext
                sensors={sensors}
                onDragStart={({ active }) => {
                  const template = templates.find(
                    template => active.id === template.name
                  );
                  template && setDragColumn(template);
                }}
                onDragEnd={({ active, over }) => {
                  if (active.id === over?.id) return;
                  const sourceIndex = templates.findIndex(
                    tmp => active.id === tmp.name
                  );
                  const targetIndex = templates.findIndex(
                    tmp => over?.id === tmp.name
                  );
                  const reorderedTemplates = arrayMove(
                    templates,
                    sourceIndex,
                    targetIndex
                  );
                  setTemplates(reorderedTemplates);
                  updateProduct({
                    templatePosition: reorderedTemplates.map(item => ({
                      id: item.id,
                      name: item.name
                    }))
                  });
                }}
              >
                <SortableContext
                  items={templates.map(template => template.name)}
                  strategy={verticalListSortingStrategy}
                >
                  <TableBody>
                    {isLoading && (
                      <TableRow>
                        <TableCell colSpan={5} padding='none'>
                          <LinearProgress />
                        </TableCell>
                      </TableRow>
                    )}
                    {templates.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant='body1' color='textSecondary'>
                            No templates available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {templates.map((template, index) => {
                      const { type: entity } = template;
                      return (
                        <ManageTemplateCardRow
                          key={index}
                          template={template}
                          showReference={showReference}
                          isLoading={isLoading}
                          renameTemplate={() => setRenameTemplate(template)}
                          getDocument={() => {
                            entityRef.current = entity;
                            getDocument({ entity });
                          }}
                          updateTemplate={() => setUpdateTemplate(template)}
                          clearTemplate={() => setClearTemplate(template)}
                          removeTemplate={() => setRemoveTemplate(template)}
                          showReferences={() => setReferenceType(entity)}
                        />
                      );
                    })}
                  </TableBody>
                </SortableContext>
                <DragOverlay
                  wrapperElement='tbody'
                  style={{ display: 'table' }}
                >
                  {dragColumn && (
                    <ManageTemplateCardRow
                      isDragging
                      className='dragging'
                      template={dragColumn}
                      showReference={showReference}
                    />
                  )}
                </DragOverlay>
              </DndContext>
            </Table>
          </TableContainer>
        </Card>
      </Grid>

      <ProductTemplateCreateDialog
        open={isOpenCreateDialog}
        productId={product.id}
        onCancel={() => setIsOpenCreateDialog(false)}
        onCreate={template => {
          setCreatedTemplate(template);
          setIsOpenCreateDialog(false);
          findProductTemplates();
        }}
      />
      {renameTemplate && (
        <ProductTemplateRenameDialog
          open={!!renameTemplate}
          template={renameTemplate}
          onCancel={() => setRenameTemplate(undefined)}
          onUpdate={template => {
            updateProductTemplate(template);
            setRenameTemplate(undefined);
          }}
        />
      )}
      {referenceType && templateReference && (
        <ReferenceDialog
          helpMessage='Help'
          type={templateReference}
          open={!!templateReference}
          title={getExportTitle(referenceType)}
          onClose={() => setReferenceType(undefined)}
        />
      )}
      {updateTemplate && (
        <AssignDocumentTemplateDialog
          open={!!updateTemplate}
          product={product}
          template={updateTemplate}
          onCancel={() => setUpdateTemplate(undefined)}
          onSuccess={(template: ProductTemplate) => {
            setUpdateTemplate(undefined);
            updateTemplates(template);
            onUpdateRef.current?.();
          }}
        />
      )}
      {clearTemplate && (
        <ConfirmDialog
          open={!!clearTemplate}
          title='Clear Document Template'
          message={`Are you sure you want to clear "${
            clearTemplate.name || getExportTitle(clearTemplate.type)
          }" template?`}
          onCancel={() => setClearTemplate(undefined)}
          onConfirm={() => {
            updateProductTemplate({ fileId: null });
            setClearTemplate(undefined);
          }}
        />
      )}
      {removeTemplate && (
        <ConfirmDialog
          open={!!removeTemplate}
          confirmLabel='Remove'
          title='Delete Product Template'
          message={`Are you sure you want to delete "${
            removeTemplate.name || getExportTitle(removeTemplate.type)
          }" template? This action cannot be undone.`}
          onCancel={() => setRemoveTemplate(undefined)}
          onConfirm={() => {
            deleteProductTemplate();
            setRemoveTemplate(undefined);
          }}
        />
      )}

      <AppSnackbar
        severity='success'
        open={!!createdTemplate}
        onClose={() => setCreatedTemplate(undefined)}
      >
        Product Template "{createdTemplate?.name}" created.
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didDelete && !!deletedProductTemplate}
        onClose={() => setDidDelete(false)}
      >
        Product Template "{deletedProductTemplate?.name}" deleted.
      </AppSnackbar>
      <AppSnackbar severity='error' open={!!error}>
        {error?.message || 'An error occurred while exporting the document.'}
      </AppSnackbar>
    </Grid>
  );
};

export default ManageTemplateCard;

const StyledTableRow = styled(TableRow)`
  &.dragging {
    opacity: 0.8;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2),
      0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12);
  }
  .drag-handle {
    margin-top: 6px;
    cursor: grab;
    opacity: 0.25;
    transition: opacity 300ms;
    &:hover {
      opacity: 1;
    }
    svg {
      color: #bbb;
    }
  }
  .drag-cell {
    width: 40px;
  }
`;

export interface ManageTemplateCardRowProps {
  template: ProductTemplate;
  showReference?: boolean;
  isLoading?: boolean;
  isDragging?: boolean;
  className?: string;
  renameTemplate?: () => void;
  getDocument?: () => void;
  updateTemplate?: () => void;
  clearTemplate?: () => void;
  showReferences?: () => void;
  removeTemplate?: () => void;
}

export const ManageTemplateCardRow: React.FC<ManageTemplateCardRowProps> = ({
  template,
  showReference,
  isLoading,
  isDragging,
  className,
  renameTemplate,
  getDocument,
  updateTemplate,
  clearTemplate,
  showReferences,
  removeTemplate
}) => {
  const { file, type: entity } = template;
  const storageKey: string | undefined =
    file?.fileVersion?.storageKey ?? file?.storageKey;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: isDragging ? '' : template.name || ''
    });
  return (
    <StyledTableRow
      ref={setNodeRef}
      key={template.id}
      style={{
        opacity: isDragging ? 0.75 : undefined,
        transform: CSS.Translate.toString(transform),
        transition: transition || undefined
      }}
      className={className}
    >
      <TableCell
        className='drag-cell'
        align='center'
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon className='drag-handle' />
      </TableCell>
      <WrappedTableCell>
        {template.name || (
          <Typography variant='body2' color='textSecondary'>
            Untitled
          </Typography>
        )}
        <IconButton onClick={renameTemplate}>
          <EditIcon fontSize='small' />
        </IconButton>
      </WrappedTableCell>
      <WrappedTableCell>{getExportTitle(entity)}</WrappedTableCell>
      <WrappedTableCell>
        {file?.path ? (
          <Link to={`/portal/documents/file/task/${file.taskId}`}>
            {file.path}
          </Link>
        ) : (
          <Typography variant='body2' color='textSecondary'>
            Default
          </Typography>
        )}
      </WrappedTableCell>
      <StyledTableCell>
        {storageKey ? (
          <DocumentDownloadButton
            title={file?.name ?? ''}
            storageKey={storageKey}
          />
        ) : (
          <Button
            color='primary'
            variant='contained'
            disabled={isLoading}
            endIcon={
              isLoading ? (
                <CircularProgress color='primary' size={20} />
              ) : (
                <GetAppIcon />
              )
            }
            onClick={getDocument}
          >
            Download
          </Button>
        )}

        <Button color='primary' variant='contained' onClick={updateTemplate}>
          Select
        </Button>
        <Button
          color='primary'
          variant='contained'
          disabled={!file}
          onClick={clearTemplate}
        >
          Clear
        </Button>
        {showReference && (
          <Button
            color='primary'
            variant='contained'
            endIcon={<HelpOutlineIcon />}
            disabled={!getProductTemplateReference(entity)}
            onClick={showReferences}
          >
            Reference
          </Button>
        )}
        <IconButton onClick={removeTemplate}>
          <CloseIcon />
        </IconButton>
      </StyledTableCell>
    </StyledTableRow>
  );
};
